import { createSelector, createReducer } from '@reduxjs/toolkit';
import {
  getIncludedData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import {
  addInvitationSourceMappingsToStore,
  deleteInvitationSourceMappingsSuccess,
  deleteInvitationSourceSuccess,
  getAllHearingsSuccess,
  getHearingSuccess,
  getPaginatedHearingsSuccess,
  removeInvitationSourceMappingsFromStore,
  uploadCsvInviteesSuccess,
  uploadExcelInviteesSuccess,
  uploadInvitationGroupInviteesSuccess,
  uploadPersonalInviteesSuccess,
} from '../actions/api';
import {
  HEARINGROLE_INVITEE,
  INVITATIONSOURCEMAPPING_INCLUDE,
} from '../../constants/api';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import {
  allUserHearingRolesWithRoleByHearingIdSelector,
  userHearingRolesByIdsSelector,
} from './userHearingRoleSlice';
import {
  allCompanyHearingRolesWithRoleByHearingIdSelector,
  companyHearingRolesByIdsSelector,
} from './companyHearingRoleSlice';

// -------- Overview --------

// The following endpoints exist
//  - No direct endpoint exist

// InvitationSource is included in the following endpoints
//  - GET /hearing
//  - GET /hearing/{hearingId}

// -------- Reducer --------
const invitationSourceMappingReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationSourceMappings = data.filter(
          (include) => include.type === INVITATIONSOURCEMAPPING_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationSourceMappings);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationSourceMappings = data.filter(
          (include) => include.type === INVITATIONSOURCEMAPPING_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationSourceMappings);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationSourceMappings = data.filter(
          (include) => include.type === INVITATIONSOURCEMAPPING_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationSourceMappings);
      })
      .addCase(uploadCsvInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationSourceMappings = data.filter(
          (include) => include.type === INVITATIONSOURCEMAPPING_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationSourceMappings);
      })
      .addCase(uploadExcelInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationSourceMappings = data.filter(
          (include) => include.type === INVITATIONSOURCEMAPPING_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationSourceMappings);
      })
      .addCase(uploadPersonalInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationSourceMappings = data.filter(
          (include) => include.type === INVITATIONSOURCEMAPPING_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationSourceMappings);
      })
      .addCase(uploadInvitationGroupInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationSourceMappings = data.filter(
          (include) => include.type === INVITATIONSOURCEMAPPING_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationSourceMappings);
      })
      .addCase(deleteInvitationSourceSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationSourceMappings = data.filter(
          (include) => include.type === INVITATIONSOURCEMAPPING_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationSourceMappings);
      })
      .addCase(deleteInvitationSourceMappingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationSourceMappings = data.filter(
          (include) => include.type === INVITATIONSOURCEMAPPING_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationSourceMappings);
      })
      .addCase(addInvitationSourceMappingsToStore, (state, action) => {
        return updateDtoList(state, 'all', action.payload);
      })
      .addCase(removeInvitationSourceMappingsFromStore, (state, action) => {
        const idsToRemove = action.payload;
        return removeItemFromList(state, 'all', idsToRemove);
      });
  },
);

// -------- Sagas ---------

// -------- Selectors --------
const selectGetAllInvitationSourceMappings = (state) =>
  selectAllFromApiState(state, 'invitationSourceMapping');

const allInvitationSourceMappingsSelector = createSelector(
  selectGetAllInvitationSourceMappings,
  (invitationSourceMappings) => invitationSourceMappings,
);

const allInvitationSourceMappingsByHearingIdSelector = (hearingId) =>
  createSelector(
    [
      allInvitationSourceMappingsSelector,
      allUserHearingRolesWithRoleByHearingIdSelector(
        HEARINGROLE_INVITEE,
        hearingId,
      ),
      allCompanyHearingRolesWithRoleByHearingIdSelector(
        HEARINGROLE_INVITEE,
        hearingId,
      ),
    ],
    (invitationSourceMappings, userHearingRoles, companyHearingRoles) => {
      const userHearingRoleIds = new Set(
        userHearingRoles.map((uhr) => getEntityId(uhr)),
      );
      const companyHearingRoleIds = new Set(
        companyHearingRoles.map((chr) => getEntityId(chr)),
      );

      return invitationSourceMappings.filter((invitationSourceMapping) => {
        const userRoleId = getRelId(invitationSourceMapping, 'userHearingRole');
        const companyRoleId = getRelId(
          invitationSourceMapping,
          'companyHearingRole',
        );

        return (
          userHearingRoleIds.has(userRoleId) ||
          companyHearingRoleIds.has(companyRoleId)
        );
      });
    },
  );

const allInvitationSourceMappingsByUserOrCompanyHearingRoleIds = (
  userHearingRoleIds,
  companyHearingRoleIds,
) =>
  createSelector(
    [
      allInvitationSourceMappingsSelector,
      userHearingRolesByIdsSelector(userHearingRoleIds),
      companyHearingRolesByIdsSelector(companyHearingRoleIds),
    ],
    (invitationSourceMappings, userHearingRoles, companyHearingRoles) => {
      const userHearingRoleIds = new Set(
        userHearingRoles.map((uhr) => getEntityId(uhr)),
      );
      const companyHearingRoleIds = new Set(
        companyHearingRoles.map((chr) => getEntityId(chr)),
      );

      return invitationSourceMappings.filter((invitationSourceMapping) => {
        return invitationSourceMappings.filter((invitationSourceMapping) => {
          const userRoleId = getRelId(
            invitationSourceMapping,
            'userHearingRole',
          );
          const companyRoleId = getRelId(
            invitationSourceMapping,
            'companyHearingRole',
          );

          return (
            userHearingRoleIds.has(userRoleId) ||
            companyHearingRoleIds.has(companyRoleId)
          );
        });
      });
    },
  );

// -------- Exporting --------
export {
  allInvitationSourceMappingsSelector,
  allInvitationSourceMappingsByHearingIdSelector,
  allInvitationSourceMappingsByUserOrCompanyHearingRoleIds,
};
export default invitationSourceMappingReducer;
